import axios from 'axios'
import { env } from '@/config/env'

/**
 * Public tenant branding JSON (strings plus supportedLocales array).
 * Other keys may exist for forward compatibility.
 */
export type TenantBrandingRecord = Record<string, string | string[] | undefined> & {
  supportedLocales?: string[]
}

export function tenantBrandingString(record: TenantBrandingRecord, key: string): string {
  const v = record[key]
  return typeof v === 'string' ? v : ''
}

export type PublicTenantBrandingPayload = {
  branding: TenantBrandingRecord
  brandingRevision: number
  cpEntitlements?: string[]
  seatingProActive?: boolean
  giftCardsActive?: boolean
  vendorSystemActive?: boolean
  analyticsActive?: boolean
}

/**
 * Fetches the public branding snapshot (no auth), including monotonic revision for cache/offline checks.
 */
export async function fetchPublicTenantBranding(): Promise<PublicTenantBrandingPayload> {
  const res = await axios.get<{
    branding: TenantBrandingRecord
    brandingRevision?: number
    cpEntitlements?: unknown
    seatingProActive?: unknown
    giftCardsActive?: unknown
    vendorSystemActive?: unknown
    analyticsActive?: unknown
  }>(`${env.apiUrl}/api/v1/public/tenant-branding`, { timeout: 15000 })
  const b = res.data?.branding
  if (!b || typeof b !== 'object' || Array.isArray(b)) {
    return {
      branding: {},
      brandingRevision: 0,
      cpEntitlements: [],
      seatingProActive: false,
      giftCardsActive: false,
      vendorSystemActive: false,
    }
  }
  const brandingRevision = Number(res.data?.brandingRevision)
  const rawEnt = res.data?.cpEntitlements
  const cpEntitlements = Array.isArray(rawEnt) ? rawEnt.map((x) => String(x)) : []
  const spa = res.data?.seatingProActive
  const seatingProActive =
    spa === true || (Array.isArray(rawEnt) && rawEnt.map(String).includes('addon:seating_pro'))
  const gca = res.data?.giftCardsActive
  const giftCardsActive =
    gca === true || cpEntitlements.includes('addon:gift_cards')
  const aa = res.data?.analyticsActive
  const vsa = res.data?.vendorSystemActive
  const vendorSystemActive =
    vsa === true || cpEntitlements.includes('addon:vendor_system')
  const analyticsActive =
    aa === true || cpEntitlements.includes('addon:analytics')
  return {
    branding: b as TenantBrandingRecord,
    brandingRevision: Number.isFinite(brandingRevision) ? brandingRevision : 0,
    cpEntitlements,
    seatingProActive,
    giftCardsActive,
    vendorSystemActive,
    analyticsActive,
  }
}
